import {
  CredentialAnalysisRequirement,
  SigningRequirementEnum,
  StepState,
  StepType,
} from "graphql_globals";
import { useFeatureFlag } from "common/feature_gating";
import { segmentTrack } from "util/segment";
import { SEGMENT_EVENTS } from "constants/analytics";
import { captureException } from "util/exception";
import { useViewer } from "util/viewer_wrapper";

import type {
  SignerStepsViewer_signerStepsV2 as Step,
  SignerStepsViewer_signerStepsV2_CredentialAnalysisStep as CredentialAnalysisStep,
} from "./index.viewer.graphql";
import type { SignerStepsBundle as DocumentBundle } from "./index.bundle.graphql";

export type MapStepToRouteParams = {
  step?: Step;
  documentBundle: DocumentBundle;
  meetingId?: string;
};

const getValidSteps = (steps: Step[]) => {
  // TODO: See if we can stop generating this step on the backend
  return steps.filter(({ stepType }) => stepType !== StepType.EMAIL);
};

export const getCurrentStep = ({
  steps,
  currentStepId,
  currentStepIndex,
}: {
  steps?: Step[];
  currentStepId?: string;
  currentStepIndex?: number;
}) => {
  if (!steps) {
    return undefined;
  }
  const validSteps = getValidSteps(steps);

  if (currentStepId) {
    const currentStep = validSteps.find(({ id }) => {
      return id === currentStepId;
    });
    if (currentStep) {
      return currentStep;
    }
    if (currentStepIndex !== undefined && currentStepIndex < validSteps.length) {
      return validSteps[currentStepIndex];
    }
    segmentTrack(SEGMENT_EVENTS.CURRENT_STEP_NOT_FOUND, {
      expected_step_id: currentStepId,
      all_step_ids: validSteps.map(({ id }) => id),
    });
  }
  if (currentStepIndex !== undefined && currentStepIndex < validSteps.length) {
    return validSteps[currentStepIndex];
  }

  segmentTrack(SEGMENT_EVENTS.CURRENT_STEP_NOT_SET, {
    currentStepId,
    currentStepIndex,
    all_steps: validSteps.map(({ id, state }) => `${id}__${state}`),
  });

  return (
    validSteps.find(({ state }) => {
      return state === StepState.IN_PROGRESS;
    }) ||
    validSteps.find(({ state }) => {
      return state === StepState.QUEUED;
    }) ||
    validSteps[validSteps.length - 1]
  );
};

export const getNextStep = (currentStep: Step | undefined, steps: Step[]) => {
  const validSteps = getValidSteps(steps);
  const nextStepIndex =
    validSteps.findIndex((step) => {
      return step.id === currentStep?.id;
    }) + 1;
  return nextStepIndex < steps.length
    ? ([steps[nextStepIndex], nextStepIndex] as const)
    : ([undefined, undefined] as const);
};

const isCredentialAnalysisStep = (step: Step): step is CredentialAnalysisStep => {
  return (
    step.__typename === "CredentialAnalysisStep" && step.stepType === StepType.CREDENTIAL_ANALYSIS
  );
};

export const isTrustedRefereeMeeting = (step?: Step) => {
  return (
    step?.__typename === "MeetingStepV2" &&
    step.stepType === StepType.MEETING &&
    step.isTrustedRefereeMeeting
  );
};

export const stepRequiresSelfie = (step?: Step) => {
  if (!step) {
    return false;
  }
  return isCredentialAnalysisStep(step)
    ? [
        CredentialAnalysisRequirement.BIOMETRIC,
        CredentialAnalysisRequirement.BIOMETRIC_PS1583,
      ].includes(step.credentialAnalysisRequirement)
    : false;
};

export const hasMeetingStep = (steps: Step[]) => {
  return steps.some((step) => step.stepType === StepType.MEETING);
};

// this map does not include the EMAIL step
const mapStepToRoute = ({
  step,
  documentBundle,
  viewerUserId,
  consolidatedRonTemplateEnabled,
  personaPremeetingCaptureEnabled,
}: {
  step?: Step;
  documentBundle: DocumentBundle;
  viewerUserId?: string;
  consolidatedRonTemplateEnabled?: boolean;
  personaPremeetingCaptureEnabled?: boolean;
}) => {
  const { id, isMortgage, signAhead, participants } = documentBundle;
  const viewerParticipant = viewerUserId
    ? participants?.find((p) => p?.userId === viewerUserId)
    : undefined;
  // use /sign for mortgage transactions except if the signingRequirement = ESIGN
  const isSignRoute =
    isMortgage &&
    signAhead &&
    viewerParticipant?.signingRequirement !== SigningRequirementEnum.ESIGN;
  switch (step?.stepType) {
    case StepType.TECH_CHECK:
      return `/bundle/${id}/twilio-tech-check`;
    case StepType.KBA:
      return `/bundle/${id}/kba-quiz`;
    case StepType.CREDENTIAL_ANALYSIS: {
      const CAPath =
        stepRequiresSelfie(step) || consolidatedRonTemplateEnabled
          ? "identity-verification"
          : !personaPremeetingCaptureEnabled
            ? "id_check/primary_front"
            : "credential-analysis";
      return `/bundle/${id}/${CAPath}`;
    }
    case StepType.MEETING:
      return `/bundle/${id}/meeting_request`;
    case StepType.DOCUMENT:
      return isSignRoute ? `/bundle/${id}/sign` : `/bundle/${id}/prepare`;
    case StepType.PERSONAL_DETAILS:
      return `/bundle/${id}/customer-details`;
    default:
      segmentTrack(SEGMENT_EVENTS.UNKNOWN_NEXT_STEP, { step });
      captureException(new Error(`Unknown next step: ${step?.stepType}`));
      return "/";
  }
};

/**
 * Returns the most recent step of the given type before the current step.
 * Used to go back to a particular previous step that the user may have already completed.
 */
export function getPreviousStepByStepType(
  steps: Step[],
  currentStepId: string,
  stepType: StepType,
) {
  const validSteps = getValidSteps(steps);
  const reversedSteps = validSteps.slice().reverse(); // create a copy of the steps array and reverse it
  const currentStepIndex = reversedSteps.findIndex((step) => step.id === currentStepId);

  // find the first step of the given type after the current step (in the reversed array)
  const reversedStepsAfterCurrent = reversedSteps.slice(currentStepIndex);
  const previousStepIndex = reversedStepsAfterCurrent.findIndex(
    (step) => step.stepType === stepType,
  );
  if (previousStepIndex === -1) {
    return [undefined, undefined] as const;
  }
  const previousStep = reversedStepsAfterCurrent[previousStepIndex];
  return [previousStep, validSteps.findIndex((step) => step.id === previousStep.id)] as const;
}

export function getPreviousStep(steps: Step[], currentStepId: string) {
  const validSteps = getValidSteps(steps);
  const currentStepIndex = validSteps.findIndex((step) => step.id === currentStepId);
  return steps[currentStepIndex - 1];
}

export const useMapStepToRoute = () => {
  const personaPremeetingCaptureEnabled = useFeatureFlag("persona-premeeting-capture");
  const consolidatedRonTemplateEnabled = useFeatureFlag("persona-ron-consolidated-template");
  const { viewer } = useViewer();

  return (props: MapStepToRouteParams) =>
    mapStepToRoute({
      ...props,
      viewerUserId: viewer.user?.id,
      personaPremeetingCaptureEnabled,
      consolidatedRonTemplateEnabled,
    });
};
